/**
 * Purchase orders — the decisions, with no I/O. See docs/architecture.md D8
 * and §4 ("approval limits become tenant configuration").
 *
 * Two things live here that the prototype had scattered through a render
 * function: what an order costs and whether the person raising it may send it
 * unattended, and how many units to propose against par. Both are money
 * decisions, both are asserted in tests, and neither should be reimplemented
 * per screen.
 *
 * **Money is in whole cents throughout.** The prototype used dollar floats and
 * compared `ordSub * 1.1` against a dollar limit; the org settings already
 * store `orderLimitsCents`, so keeping dollars here would mean converting at
 * every comparison. `toCents` in calc.ts exists for the same reason. Field
 * names carry the `Cents` suffix so a mixed-unit bug is visible at the call
 * site rather than three decimal places into a subtotal.
 */
import { isOrgAdmin } from "./permissions.js";
// -------------------------------------------------------------------- types
/**
 * The three order types, from the prototype's `ORDERTYPES`.
 *
 * A candidate for tenant configuration eventually (§4 — anything the prototype
 * hardcodes is), but the multiplier below only makes sense against these three
 * meanings, so they stay together and stay in code for now.
 */
export const ORDER_TYPES = [
    "Weekly standing",
    "Mid-week top-up",
    "Event or function",
];
/** Human note per type, shown beside the picker on screen 10. */
export const ORDER_TYPE_NOTES = {
    "Weekly standing": "Par top-up against the standing order sheet",
    "Mid-week top-up": "Short-dated lines only, delivered next run",
    "Event or function": "Above par, booked against a function sheet",
};
/**
 * How the par shortfall is scaled per type — the prototype's `ORDERMULT`.
 *
 * A mid-week top-up covers half the gap because another delivery is coming; a
 * function orders double because the shortfall is measured against normal
 * trade, not the night being catered.
 */
export const ORDER_MULTIPLIER = {
    "Weekly standing": 1,
    "Mid-week top-up": 0.5,
    "Event or function": 2,
};
export function isOrderType(value) {
    return ORDER_TYPES.includes(value);
}
/** How a supplier item is counted and delivered. */
export const PACK_UNITS = ["btl", "case", "bag", "block", "tray"];
export function isPackUnit(value) {
    return PACK_UNITS.includes(value);
}
/**
 * Order lifecycle.
 *
 * `submitted` and `approved` are separate from `queued` on purpose. Approval is
 * a human decision recorded against the item; queueing is the side effect that
 * follows it. Keeping them apart means a failed enqueue leaves an approved
 * order that can be retried, rather than an order that claims to be sent and
 * is not.
 */
export const ORDER_STATUSES = [
    "draft",
    "submitted",
    "approved",
    "queued",
    "sent",
    // The invoice has arrived and is linked to an expense (screen 11).
    "received",
    "cancelled",
];
export function isOrderStatus(value) {
    return ORDER_STATUSES.includes(value);
}
// ---------------------------------------------------------------- subtotals
/** Australian GST. Matches `GST_MULTIPLIER` in calc.ts. */
const GST_RATE = 0.1;
/**
 * What the order costs.
 *
 * GST is rounded once, on the total, rather than per line. Rounding each line
 * and summing drifts by a cent or two against the supplier's own invoice,
 * which is exactly the sort of discrepancy that costs someone twenty minutes
 * on the phone.
 */
export function orderSubtotals(lines) {
    const subtotalExGstCents = lines.reduce((sum, line) => sum + line.qty * line.unitCostCents, 0);
    return {
        subtotalExGstCents,
        subtotalIncGstCents: Math.round(subtotalExGstCents * (1 + GST_RATE)),
    };
}
/**
 * Which limit tier applies to this caller at this venue.
 *
 * An org owner or admin gets the owner tier: §4 grants them everything except
 * billing and deleting the org, and holding them to a manager's spend limit
 * would contradict that. Returns undefined for someone with no standing at the
 * venue — they cannot order at all, which is a permission failure rather than
 * a limit of zero, and `order:create` will already have refused them.
 */
export function limitRoleFor(actor, venueId) {
    if (isOrgAdmin(actor))
        return "owner";
    if (!venueId)
        return undefined;
    const role = actor.venueRoles[venueId];
    if (role === "manager")
        return "manager";
    if (role === "bartender")
        return "bartender";
    return undefined;
}
/**
 * The prototype's `LIMITS = {Bartender: 250, Manager: 1500, Owner: 100000}`,
 * in cents. Used only as the default when an org has not set its own — §4 says
 * these become tenant configuration, and the seed already writes them onto
 * `ORG#<id> / META`.
 */
export const DEFAULT_ORDER_LIMITS = {
    bartender: 25_000,
    manager: 150_000,
    owner: 10_000_000,
};
/**
 * The limits an org actually acts under, given whatever its META row holds.
 *
 * Every reader has to answer "what if the block is missing or partial", and
 * until now each answered differently: `orderLimits()` in the orders route
 * fell back to `DEFAULT_ORDER_LIMITS` per role, while the settings screen
 * read a missing role as `0` and then **saved that zero back**. One visit to
 * screen 12 on an org with no limits block therefore replaced the defaults
 * with `{bartender: 0, manager: 0, owner: 0}` — and because those zeros are
 * present, the route stopped falling back, so no order could be approved by
 * anyone, including the owner. Nothing warned, and nothing in the UI said
 * where the zeros came from.
 *
 * So the merge lives here and every reader uses it. A value only counts when
 * it is a non-negative integer number of cents; anything else (absent, null,
 * a string from a hand-edited item, NaN) means "not set" and takes the
 * default. A deliberate `0` is still honoured — an org may well want every
 * bartender order to need a nod — it just can no longer arrive by accident.
 */
export function settledOrderLimits(stored) {
    const block = typeof stored === "object" && stored !== null ? stored : {};
    // `Object.hasOwn`, not a bare lookup: this takes `unknown`, and an object
    // whose prototype carries an `owner` must not be read as one that sets it.
    const settled = (role) => {
        const value = Object.hasOwn(block, role) ? block[role] : undefined;
        return typeof value === "number" && Number.isInteger(value) && value >= 0
            ? value
            : DEFAULT_ORDER_LIMITS[role];
    };
    return { bartender: settled("bartender"), manager: settled("manager"), owner: settled("owner") };
}
/** Ascending by authority, so "who could approve this" reads in order. */
const LIMIT_ROLES = ["bartender", "manager", "owner"];
export function limitCentsFor(role, limits) {
    return limits[role];
}
/**
 * The whole decision about one draft order, in one place.
 *
 * The limit is checked against the **GST-inclusive** subtotal, as the prototype
 * did (`ordSub * 1.1 > limit`). That is the number that leaves the bank
 * account, so it is the number a spending limit should be about. The comparison
 * is strictly greater, so an order landing exactly on the limit is within it.
 *
 * The supplier minimum, by contrast, is checked ex GST — that is how suppliers
 * quote them ("$400 ex GST"), and treating it any other way would let an order
 * through that the rep will bounce.
 */
export function reviewOrder(input) {
    const { subtotalExGstCents, subtotalIncGstCents } = orderSubtotals(input.lines);
    const limitCents = limitCentsFor(input.role, input.limits);
    const withinLimit = subtotalIncGstCents <= limitCents;
    const minimumExGstCents = input.minimumExGstCents ?? 0;
    const isEmpty = subtotalExGstCents <= 0;
    const underMinimum = !isEmpty && subtotalExGstCents < minimumExGstCents;
    const approverRoles = withinLimit
        ? []
        : LIMIT_ROLES.filter((role) => limitCentsFor(role, input.limits) >= subtotalIncGstCents);
    const canSubmit = !isEmpty && !underMinimum;
    return {
        subtotalExGstCents,
        subtotalIncGstCents,
        limitCents,
        withinLimit,
        approverRoles,
        underMinimum,
        minimumShortfallCents: underMinimum
            ? minimumExGstCents - subtotalExGstCents
            : 0,
        canSubmit,
        outcome: isEmpty
            ? "empty"
            : underMinimum
                ? "held-under-minimum"
                : withinLimit
                    ? "auto-approve"
                    : "needs-approval",
    };
}
/**
 * Convert a `STOCK#` holding into the units par is counted in.
 *
 * Returns undefined when there is no holding to convert, so the caller can
 * pass it straight to `heldUnits` and keep "never counted" distinct from
 * "counted, none left".
 */
export function heldUnitsFrom(heldMl, unitMl) {
    if (heldMl === null || heldMl === undefined)
        return undefined;
    if (!unitMl || unitMl <= 0)
        return undefined;
    return heldMl / unitMl;
}
/**
 * Propose quantities to bring a supplier's lines back to par.
 *
 * Quantities come out in whole packs, always rounded up: a bar cannot order
 * two-thirds of a case, and rounding down leaves the shortfall that prompted
 * the order in the first place.
 *
 * An item already at or above par proposes **zero**, which is where this
 * departs from the prototype. The prototype had no stock figure in the
 * calculation and so always proposed at least one pack; production knows what
 * is held, and proposing a case of something the venue is full of is how a
 * cool room ends up with eleven bottles of Fernet.
 */
export function suggestTopUp(items, type) {
    const multiplier = ORDER_MULTIPLIER[type];
    return items.map((item) => {
        const packSize = item.packSize > 0 ? item.packSize : 1;
        const shortfallUnits = item.heldUnits === undefined
            ? item.par
            : Math.max(0, item.par - item.heldUnits);
        const packsNeeded = Math.ceil(shortfallUnits / packSize);
        // Only clamp to a minimum of one pack when something is actually short.
        // `Math.max(1, …)` applied unconditionally is what would resurrect the
        // prototype's "always order something" behaviour.
        const qty = packsNeeded > 0 ? Math.max(1, Math.ceil(packsNeeded * multiplier)) : 0;
        return {
            ...item,
            heldUnits: item.heldUnits ?? null,
            shortfallUnits,
            qty,
        };
    });
}
// ------------------------------------------------------------- dedup key
/**
 * The SQS FIFO `MessageDeduplicationId` for an order, also stored on the
 * `ORDER#` item so a replayed send is detectable at the data layer (§5).
 *
 * Derived from the identity of the order rather than a hash of its contents.
 * An order is approved once and must be sent once; if its lines somehow
 * changed between approval and send, that is a bug to catch, not a reason to
 * let a second order out the door.
 *
 * SQS allows up to 128 characters of alphanumerics and punctuation. Ids are
 * generated identifiers, so sanitising is belt-and-braces — but a `#` in a key
 * would be rejected at send time, which is a poor place to discover it.
 */
export function dedupKeyFor(venueId, orderId) {
    return `${venueId}-${orderId}`.replace(/[^A-Za-z0-9_.:-]/g, "-").slice(0, 128);
}
//# sourceMappingURL=orders.js.map